import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Code, Save, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SchemaManager = () => {
  const [globalSchema, setGlobalSchema] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/site-config`);
      setGlobalSchema(res.data?.global_schema || "");
    } catch (err) {
      toast.error("Failed to fetch schema config");
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = (val) => {
    setGlobalSchema(val);
    if (!val.trim()) {
      setValidationError("");
      return;
    }
    try {
      JSON.parse(val);
      setValidationError("");
    } catch (e) {
      setValidationError("Invalid JSON format. Please check your syntax.");
    }
  };

  const saveConfig = async () => {
    if (validationError) {
      toast.error("Please fix validation errors before saving.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/site-config`, { global_schema: globalSchema }, { withCredentials: true });
      toast.success("Global Schema updated");
    } catch (error) {
      toast.error("Failed to save schema");
    } finally {
      setSaving(false);
    }
  };

  const insertTemplate = (type) => {
    let template = "";
    if (type === "organization") {
      template = `{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Jamadhan Stock Market Institute",
  "url": "https://jamadhan.com",
  "logo": "https://jamadhan.com/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-0000000000",
    "contactType": "customer service"
  }
}`;
    } else if (type === "website") {
      template = `{
  "@context": "https://schema.org/",
  "@type": "WebSite",
  "name": "Jamadhan",
  "url": "https://jamadhan.com/",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://jamadhan.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
}`;
    }
    
    if (globalSchema && globalSchema.trim().startsWith("[")) {
      // It's an array
      try {
        const arr = JSON.parse(globalSchema);
        arr.push(JSON.parse(template));
        handleValidate(JSON.stringify(arr, null, 2));
      } catch(e) {
        handleValidate(template);
      }
    } else if (globalSchema && globalSchema.trim().startsWith("{")) {
      // It's a single object, convert to array
      try {
        const obj = JSON.parse(globalSchema);
        handleValidate(JSON.stringify([obj, JSON.parse(template)], null, 2));
      } catch(e) {
        handleValidate(template);
      }
    } else {
      handleValidate(template);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <Code className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Global Schema Markup (JSON-LD)
            </CardTitle>
            <p className="text-xs text-gray-500 dark:text-[#A1A1AA] mt-1">Add Organization or Website schema to improve rich results in Google Search. This injects on all pages.</p>
          </div>
          <Button onClick={saveConfig} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl h-10 px-4">
            <Save className="w-4 h-4 mr-2" /> Save Schema
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={() => insertTemplate("organization")} className="text-xs border-gray-200 dark:border-white/10 text-gray-500 dark:text-[#A1A1AA] hover:text-gray-900 dark:text-white bg-transparent">
              Insert Organization Schema
            </Button>
            <Button variant="outline" size="sm" onClick={() => insertTemplate("website")} className="text-xs border-gray-200 dark:border-white/10 text-gray-500 dark:text-[#A1A1AA] hover:text-gray-900 dark:text-white bg-transparent">
              Insert Website Schema
            </Button>
          </div>

          <div className="relative">
            <Textarea 
              value={globalSchema} 
              onChange={e => handleValidate(e.target.value)} 
              placeholder='{ "@context": "https://schema.org", "@type": "Organization", ... }' 
              className={`bg-[#F3F4F6] dark:bg-[#050505] border-${validationError ? 'red-500/50' : 'white/[0.06]'} text-gray-900 dark:text-white font-mono text-sm min-h-[400px] leading-relaxed`}
            />
            {validationError ? (
              <div className="absolute bottom-3 right-3 text-red-500 text-xs flex items-center bg-red-500/10 px-2 py-1 rounded">
                <AlertTriangle className="w-3 h-3 mr-1" /> {validationError}
              </div>
            ) : globalSchema.trim() ? (
              <div className="absolute bottom-3 right-3 text-emerald-600 dark:text-emerald-400 text-xs flex items-center bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-2.5 py-1 rounded-lg">
                <CheckCircle className="w-3 h-3 mr-1" /> Valid JSON
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SchemaManager;
